/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useState, useEffect } from 'react'
import {
  BarChart,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TabGroup,
  TabList,
  Tab,
} from '@tremor/react'
import { CircularProgress, IconButton } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'

import moment, { Moment } from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import { montserratStyle } from 'features/Main/NewDashboard/styles'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { getTicketScanStatsByStore } from './api'
import { useStyles } from './styles'

interface TicketScanStatsByStoreParams {
  startDate: string
  endDate: string
}

interface TicketScanStatsByStoreResponse {
  data: Array<{
    local: string
    porcentaje: number
  }>
}

// eslint-disable-next-line @typescript-eslint/member-delimiter-style
const downloadCSV = (data: Array<{ local: string; porcentaje: number }>) => {
  const headers = ['Local', 'Porcentaje de Scans']
  const csvContent = [
    headers.join(','),
    ...data.map(item => [item.local, item.porcentaje].join(',')),
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `tickets_por_local_${moment().format('YYYY-MM')}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

interface TicketsByAppByStoreProps {
  period1: { startDate: Moment; endDate: Moment }
  period2: { startDate: Moment; endDate: Moment }
  compara: boolean
  trigger: number
  storeIds?: number[]
  onLoaded?: () => void
}

interface TableData {
  local: string
  porcentaje: number | null
  porcentajeComparativo: number | null
}

interface BarChartData {
  [x: string]: string | number | null
  local: string
}

const TicketsByAppByStore: React.FC<TicketsByAppByStoreProps> = ({
  period1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  period2,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  compara,
  trigger,
  storeIds,
  onLoaded,
}) => {
  const classes = useStyles()
  const [tabIndex, setTabIndex] = useState(0)
  const [data, setData] = useState<Array<{ local: string; porcentaje: number }>>([])
  const [statsCall, statsLoading] = useApiCall<
    TicketScanStatsByStoreParams,
    TicketScanStatsByStoreResponse
  >(getTicketScanStatsByStore)

  const [mergedData, setMergedData] = useState<TableData[]>([])
  const [barChartData, setBarChartData] = useState<BarChartData[]>([])
  const [barCategories, setBarCategories] = useState<string[]>([])

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'TicketsByAppByStore' })

  const formatData = () => {
    const formattedMergedData = data.map(item => ({
      local: item.local,
      porcentaje: item.porcentaje,
      porcentajeComparativo: null,
    }))
    setMergedData(formattedMergedData)

    const formattedBarChartData = formattedMergedData.map(row => ({
      local: row.local,
      [`${period1.startDate.format('DD/MM')} - ${period1.endDate.format(
        'DD/MM/YYYY',
      )}`]: row.porcentaje,
    }))
    setBarChartData(formattedBarChartData)

    const formattedBarCategories = [
      `${period1.startDate.format('DD/MM')} - ${period1.endDate.format('DD/MM/YYYY')}`,
    ]

    setBarCategories(formattedBarCategories)
  }

  const fetchData = async () => {
    try {
      // Periodo principal - usar fechas completas
      const startDate = period1.startDate.format('YYYY-MM-DD')
      const endDate = period1.endDate.format('YYYY-MM-DD')

      const response = await statsCall({
        startDate,
        endDate,
        ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
      })
      if (response?.data) {
        const sortedData = [...response.data].sort((a, b) => b.porcentaje - a.porcentaje)
        setData(sortedData)
      }
    } catch (error) {
      console.error('Error fetching ticket statistics by store:', error)
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      const run = async () => {
        try {
          await fetchData()
        } finally {
          if (onLoaded) {
            onLoaded()
          }
        }
      }
      run()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  useEffect(() => {
    formatData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const barColors = ['#26342E', '#A7B7A3']

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <span className="font-bold text-base block mb-4" style={montserratStyle}>
          Tickets por Local -{' '}
          {`${period1.startDate.format('DD/MM')} - ${period1.endDate.format('DD/MM/YYYY')}`}
        </span>
        <IconButton
          disabled={statsLoading}
          className="ml-2"
          aria-label="download"
          onClick={() => downloadCSV(data)}
        >
          <GetAppIcon />
        </IconButton>
      </div>

      <div className="mt-8">
        <TabGroup index={tabIndex} onIndexChange={setTabIndex}>
          <TabList className="mt-8">
            <Tab className={classes.tab}>Gráfico</Tab>
            <Tab className={classes.tab}>Tabla</Tab>
          </TabList>
        </TabGroup>

        {statsLoading ? (
          <div className="flex items-center justify-center py-8">
            <CircularProgress size={40} />
          </div>
        ) : (
          <div className="mt-4">
            {tabIndex === 0 ? (
              <div className="overflow-x-auto" id="bar-chart-locales-container">
                <div style={{ minWidth: 600 }}>
                  <BarChart
                    className="mt-6 w-full"
                    id="bar-chart-locales"
                    data={barChartData}
                    index="local"
                    categories={barCategories}
                    colors={barColors.slice(0, barCategories.length)}
                    valueFormatter={(number: number) =>
                      number !== null && number !== undefined
                        ? `${number.toFixed(2)}%`
                        : 'Sin datos'
                    }
                    yAxisWidth={60}
                    noDataText="No hay datos disponibles para mostrar en este momento."
                  />
                </div>
              </div>
            ) : (
              <Table className="mt-4">
                {mergedData.length > 0 ? (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Local</TableHeaderCell>
                        <TableHeaderCell>Porcentaje de Scans</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mergedData.map(item => (
                        <TableRow key={item.local}>
                          <TableCell>{item.local}</TableCell>
                          <TableCell>
                            {item.porcentaje !== null && item.porcentaje !== undefined
                              ? `${item.porcentaje.toFixed(2)}%`
                              : 'Sin datos'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    No hay datos disponibles para mostrar en este momento.
                  </div>
                )}
              </Table>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

TicketsByAppByStore.defaultProps = {
  storeIds: undefined,
  onLoaded: undefined,
}

export default TicketsByAppByStore
